import { isAfter } from 'date-fns';

import type { RentAmountChangeRequest } from '@zg-rentals/ts-rent-payment-client';

import { getNextDueDate, getRemainingTermLengthInMonths } from '@common/paymentsUtils/paymentCalculator';
import RENT_AMOUNT_CHANGE_STATUS from '../constants/RentAmountChangeConstants';
import type { ExtendedRentAmountChangeModel, INextPayableModel } from './Types';
import { coerceToDate, formatTimestampToYMDString } from '@common/paymentsUtils/dateUtils';
import { toUTCTimestamp } from '@common/paymentsUtils/dateUtils';
// We limit the maximum number of options in the dropdown for the new rent amount effective date to 12.
const RENT_AMOUNT_CHANGE_DROPDOWN_MAX = 12;

export default (
  rentAmountChangeRequest: Partial<RentAmountChangeRequest> = {},
  firstDueDate: string,
  endDateForPayments: string,
  nextPayable: INextPayableModel,
  today: Date = new Date(),
  monthlyPaymentDate,
): ExtendedRentAmountChangeModel => {
  const { effectiveDueDate, newAmount = 0, renterAcceptanceDate, expired = false } = rentAmountChangeRequest || {};

  const nextAllowedChangeDate =
    nextPayable.dueDate && isAfter(coerceToDate(nextPayable.dueDate)!, today)
      ? nextPayable.dueDate
      : getNextDueDate({ firstDueDate, today, monthlyPaymentDate });

  const nextAllowedChangeDateFormat = coerceToDate(nextAllowedChangeDate)!;

  const maxMonthsForRentAmountChange = endDateForPayments
    ? getRemainingTermLengthInMonths({ start: nextAllowedChangeDateFormat, end: endDateForPayments })
    : RENT_AMOUNT_CHANGE_DROPDOWN_MAX;

  let status: string;
  if (renterAcceptanceDate) {
    status = RENT_AMOUNT_CHANGE_STATUS.ACCEPTED;
  } else if (expired) {
    status = RENT_AMOUNT_CHANGE_STATUS.EXPIRED;
  } else if (effectiveDueDate) {
    status = RENT_AMOUNT_CHANGE_STATUS.PENDING;
  } else if (maxMonthsForRentAmountChange <= 0) {
    status = RENT_AMOUNT_CHANGE_STATUS.DISABLED;
  } else {
    status = RENT_AMOUNT_CHANGE_STATUS.ENABLED;
  }

  return {
    effectiveDueDate: formatTimestampToYMDString(toUTCTimestamp(effectiveDueDate)),
    newAmount: newAmount && Number(newAmount),
    renterAcceptanceDate: formatTimestampToYMDString(toUTCTimestamp(renterAcceptanceDate)),
    nextAllowedChangeDate,
    maxMonthsForRentAmountChange,
    status,
  };
};
